import React from "react";
import {useTranslation} from "react-i18next";
import DataTable, {type IDataTableProps} from "./DataTable";
import StructuredValue from "./StructuredValue";

const CATEGORY_ORDER = ["profile", "emails", "phones", "addresses", "documents", "preferences"];
const META_KEYS = new Set(["metadata", "__metadata"]);

const fieldValue = (value: any) =>
  value && typeof value === "object" && !Array.isArray(value) && "value" in value ? value.value : value;

const schemaDescription = (schema: Record<string, any> | undefined) =>
  schema?.description || schema?.properties?.value?.description || "";

function PersonalCategoryIcon({category}: {category: string}) {
  const paths: Record<string, React.ReactNode> = {
    profile: <><circle cx="12" cy="8" r="3.2"/><path d="M5.8 19c.7-3.2 2.8-5 6.2-5s5.5 1.8 6.2 5"/></>,
    emails: <><rect x="3.5" y="5.5" width="17" height="13" rx="2.5"/><path d="m5 7 7 5.5L19 7"/></>,
    phones: <path d="M8.2 4.2 10 8.4 7.8 10a14 14 0 0 0 6.2 6.2l1.6-2.2 4.2 1.8v2.4c0 1-.8 1.8-1.8 1.8A14 14 0 0 1 4 6c0-1 .8-1.8 1.8-1.8h2.4Z"/>,
    addresses: <><path d="M19 10c0 5-7 10-7 10S5 15 5 10a7 7 0 1 1 14 0Z"/><circle cx="12" cy="10" r="2.2"/></>,
    documents: <><path d="M7 3.5h7l4 4V20H7z"/><path d="M14 3.5V8h4M10 12h5M10 15.5h5"/></>,
    preferences: <><path d="M4 7h10M18 7h2M4 17h2M10 17h10M4 12h4M12 12h8"/><circle cx="16" cy="7" r="2"/><circle cx="8" cy="17" r="2"/><circle cx="10" cy="12" r="2"/></>,
  };
  return <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
    {paths[category] || <><circle cx="12" cy="12" r="8"/><path d="M8.5 12h7M12 8.5v7"/></>}
  </svg>;
}

function SchemaValue({value, schema, label}: {value: any; schema?: Record<string, any>; label: (field: string) => string}) {
  const properties = schema?.properties;
  if (value && typeof value === "object" && !Array.isArray(value) && properties && !("value" in value)) {
    const entries = Object.entries(value).filter(([key]) => !META_KEYS.has(key));
    if (entries.length) return <div className="personal-nested-fields">
      {entries.map(([field, nested]) => {
        const nestedSchema = properties[field];
        return <div key={field}>
          <label>{label(field)}</label>
          {schemaDescription(nestedSchema) && <small>{schemaDescription(nestedSchema)}</small>}
          <div className="read-only-field"><SchemaValue value={nested} schema={nestedSchema} label={label}/></div>
        </div>;
      })}
    </div>;
  }
  const valueSchema = schema?.properties?.value || schema;
  return <StructuredValue value={value} compact={Boolean(valueSchema?.bsonType === "object")}/>;
}

function PersonalDataDetails({record, schema}: {record: Record<string, any>; schema?: Record<string, any>}) {
  const {t} = useTranslation();
  const data = record.data && typeof record.data === "object" ? record.data : {};
  const categories = [
    ...CATEGORY_ORDER.filter(category => data[category]),
    ...Object.keys(data).filter(category => !CATEGORY_ORDER.includes(category)),
  ];
  const dataSchema = schema?.properties?.data?.properties || {};
  const label = (field: string) => t(`dataTable.fields.${field}.label`, {
    defaultValue: field.replaceAll("_", " ").replace(/([a-z])([A-Z])/g, "$1 $2"),
  });

  return <div className="personal-record">
    <div className="personal-record-summary">
      {["_id", "userId", "status", "createdAt", "updatedAt"].filter(field => record[field] !== undefined).map(field =>
        <div key={field}><small>{label(field)}</small><strong><StructuredValue value={record[field]}/></strong></div>)}
    </div>
    <div className="personal-sections">
      {categories.map(category => {
        const title = t(`personalData.categories.${category}`, {defaultValue: label(category)});
        const fieldCount = Object.keys(data[category] || {}).filter(key => !META_KEYS.has(key)).length;
        return <section key={category}>
          <header className="personal-section-heading">
            <span className="personal-section-icon"><PersonalCategoryIcon category={category}/></span>
            <strong>{title}</strong>
            <span className="personal-section-count" aria-label={`${title}: ${fieldCount}`} title={`${title}: ${fieldCount}`}>{fieldCount}</span>
          </header>
          <div className="personal-fields">
            {Object.entries(data[category] || {}).filter(([field]) => !META_KEYS.has(field)).map(([field, value]: [string, any]) => {
              const metadata = value?.metadata || value?.__metadata || data[category]?.metadata?.[field];
              const fieldSchema = dataSchema[category]?.properties?.[field];
              return <div className="personal-field" key={field}>
                <label>{label(field)}</label>
                {schemaDescription(fieldSchema) && <small className="personal-field-description">{schemaDescription(fieldSchema)}</small>}
                <div className="read-only-field"><SchemaValue value={fieldValue(value)} schema={fieldSchema?.properties?.value || fieldSchema} label={label}/></div>
                {metadata && <div className="field-metadata">
                  {metadata.verified !== undefined && <span className={metadata.verified ? "verified" : ""}>{metadata.verified ? t("personalData.verified") : t("personalData.notVerified")}</span>}
                  {(metadata.tags || []).map((tag: string) => <span key={tag}>{tag}</span>)}
                </div>}
              </div>;
            })}
          </div>
        </section>;
      })}
    </div>
  </div>;
}

export default function PersonalDataPanel({schema, ...props}: IDataTableProps & {schema?: Record<string, any>}) {
  const {t} = useTranslation();
  return <DataTable {...props}
    defaultColumns={["userId", "status", "data.profile.firstName.value", "data.profile.lastName.value"]}
    detailsTitle={t("personalData.userDetails")}
    detailsRenderer={record => <PersonalDataDetails record={record} schema={schema}/>}
  />;
}
